import React, { useState, useMemo, useEffect } from 'react';
import __ from 'utils/i18n';
import Loader from 'components/Loader';
import { SubscribersTable } from './components/SubscribersTable';
import { SubscribersReportWidget } from './components/SubscribersReportWidget';
import { SubscriberDetailPanel } from './components/SubscriberDetailPanel';
import { RequestCardUpdateModal } from './components/RequestCardUpdateModal';
import { PauseSubscriptionModal } from './components/PauseSubscriptionModal';
import { ReactivateSubscriptionModal } from './components/ReactivateSubscriptionModal';
import { CancelSubscriptionModal } from './components/CancelSubscriptionModal';
import { BulkActionsWidget } from './components/BulkActionsWidget';
import { BulkActionConfirmModal } from './components/BulkActionConfirmModal';
import { BulkActionResultsModal } from './components/BulkActionResultsModal';
import DashboardPanel from 'containers/Dashboard/components/DashboardPanel';
import ServerSideSearch from 'components/ServerSideSearch';
import SelectInput from 'components/forms/inputs/SelectInput';
import { Button } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { fetchResourceRP } from '../_shared/fetchResourceRP';
import { getSubscriptions, pauseSubscription, reactivateSubscription, cancelSubscription, sendNotification, getChargesBySubscription, bulkSubscriptionAction } from './_shared/resources';
import { getPlans } from '../Plans/_shared/resources';
import BottomAlert from 'components/BottomAlert';
import useSuccessAlert from 'hooks/useSuccessAlert';
import { notification } from 'utils/notifications';
import { BackendRpErrorResponse } from '../_shared/BackendRpErrorResponse';
import gtmEvents from 'utils/gtmEvents';

export const SubscribersTab = () => {
  const { token } = useSelector((state) => state.api);
  const merchantId = useSelector((state) => state.login.user?.merchantId);

  useEffect(() => {
    gtmEvents.recurringPaymentsPageView('subscribers', merchantId);
  }, [merchantId]);
  
  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [queryParams, setQueryParams] = useState({});
  
  const [showLoader, setShowLoader] = useState(true);
  const isFirstRender = React.useRef(true);
  
  const [periodFilter, setPeriodFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [navigatedPlan, setNavigatedPlan] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  
  // useQuery con paginación y filtros enviados al backend
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['recurringPayments', 'subscriptions', currentPage, pageSize, queryParams],
    queryFn: () => {
      const params = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        ...queryParams,
      };
      return fetchResourceRP(getSubscriptions(token), params);
    },
    enabled: !!token,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      setShowLoader(false);
      console.error('[SubscribersTab] Error loading subscriptions:', error);
      notification.error(BackendRpErrorResponse.firstErrorMessage(error));
    },
  });

  // Obtener lista de planes para el dropdown de filtros
  const { data: plansData } = useQuery({
    queryKey: ['recurringPayments', 'plansForSubscribersFilter'],
    queryFn: () => fetchResourceRP(getPlans(token), { limit: 100, offset: 0 }),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      console.error('[SubscribersTab] Error loading plans:', error);
      notification.error(BackendRpErrorResponse.firstErrorMessage(error));
    },
  });

  // Filtrar solo planes que tienen al menos 1 suscriptor
  const plansWithSubscribers = useMemo(() => {
    if (!plansData?.data) return [];
    
    return plansData.data.filter(plan => 
      (plan.activeSubscriptionsCount || 0) + 
      (plan.pausedSubscriptionsCount || 0) + 
      (plan.cancelledSubscriptionsCount || 0) > 0
    );
  }, [plansData]);

  const [items, setItems] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [subscriberCharges, setSubscriberCharges] = useState([]);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [showRequestCardUpdateModal, setShowRequestCardUpdateModal] = useState(false);
  const [subscriberForCardUpdate, setSubscriberForCardUpdate] = useState(null);
  const [showPauseSubscriptionModal, setShowPauseSubscriptionModal] = useState(false);
  const [subscriberToPause, setSubscriberToPause] = useState(null);
  const [isPausing, setIsPausing] = useState(false);
  const [showReactivateSubscriptionModal, setShowReactivateSubscriptionModal] = useState(false);
  const [subscriberToReactivate, setSubscriberToReactivate] = useState(null);
  const [isReactivating, setIsReactivating] = useState(false);
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);
  const [subscriberToCancel, setSubscriberToCancel] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkScope, setBulkScope] = useState('page');
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkResultEmails, setBulkResultEmails] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const selectedEmailsRef = React.useRef({});

  const rememberSubscriberEmail = (subscriber) => {
    const id = subscriber?.subscriptionId;
    const email = subscriber?.customer?.email || subscriber?.email;
    if (!id || !email) return;
    selectedEmailsRef.current[id] = email;
    selectedEmailsRef.current[String(id).toLowerCase()] = email;
  };

  useEffect(() => {
    setSelectedIds([]);
    selectedEmailsRef.current = {};
  }, [currentPage]);
  
  const { alertProps } = useSuccessAlert();

  React.useEffect(() => {
    const params = {};
    
    if (planFilter) {
      params.plan_id = planFilter;
    }
    
    if (statusFilter) {
      params.status = statusFilter;
    }
    
    if (periodFilter) {
      const now = new Date();
      if (periodFilter === 'lastDay') {
        params.date_from = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      } else if (periodFilter === 'last7Days') {
        params.date_from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      } else if (periodFilter === 'last30Days') {
        params.date_from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      }
      params.date_to = now.toISOString();
    }
    
    // Add search parameter - searchText is already sanitized by ServerSideSearch component
    // and only contains values that meet minLength requirement (or empty)
    if (searchText) {
      params.search = searchText;
    }
    
    setQueryParams(params);
    setSelectedIds([]);
    selectedEmailsRef.current = {};
    
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      setCurrentPage(1);
      setShowLoader(true);
    }
  }, [planFilter, statusFilter, periodFilter, searchText]);

  React.useEffect(() => {
    if (data?.data) {
      setItems(data.data);
      setShowLoader(false);
    }
  }, [data]);

  // Generar lista de planes para el dropdown (inyecta plan de "Ver todos" si falta en la API)
  const uniquePlans = useMemo(() => {
    const fromApi = (plansWithSubscribers || []).map(plan => ({
      name: plan.name,
      id: plan.planId,
    }));

    const plans = [...fromApi];
    if (navigatedPlan && !plans.some((plan) => String(plan.id) === String(navigatedPlan.id))) {
      plans.push(navigatedPlan);
    }

    return plans.sort((a, b) => a.name.localeCompare(b.name));
  }, [plansWithSubscribers, navigatedPlan]);

  const isPlanFilterInUi = useMemo(() => {
    if (!planFilter) return false;
    return uniquePlans.some((plan) => String(plan.id) === String(planFilter));
  }, [planFilter, uniquePlans]);

  const clearFilters = () => {
    setPeriodFilter('');
    setPlanFilter('');
    setNavigatedPlan(null);
    setStatusFilter('');
    setSearchText('');
  };
  
  const hasActiveFilters = periodFilter || planFilter || statusFilter || searchText;

  React.useEffect(() => {
    const savedPlanFilter = sessionStorage.getItem('subscribersPlanFilter');
    if (!savedPlanFilter) return;

    sessionStorage.removeItem('subscribersPlanFilter');

    try {
      const parsed = JSON.parse(savedPlanFilter);
      if (parsed?.id) {
        setPlanFilter(parsed.id);
        setNavigatedPlan({ id: parsed.id, name: parsed.name || parsed.id });
        return;
      }
    } catch {
      // legacy: plain planId string
    }

    setPlanFilter(savedPlanFilter);
  }, []);

  const reload = () => {
    refetch();
  };

  const handleSubscriberUpdated = (updatedSubscriber) => {
    // Actualizar el subscriber seleccionado
    setSelectedSubscriber(updatedSubscriber);
    
    // Actualizar en la lista de items si existe
    setItems(prevItems => 
      prevItems.map(item => 
        item.subscriptionId === updatedSubscriber.subscriptionId 
          ? updatedSubscriber 
          : item
      )
    );
    
    // Recargar los datos
    reload();
  };

  const handleRowClick = async (subscriber) => {
    setSelectedSubscriber(subscriber);
    setShowDetails(true);
    setSubscriberCharges([]);
    
    if (subscriber?.subscriptionId) {
      setLoadingCharges(true);
      try {
        const charges = await fetchResourceRP(() => getChargesBySubscription(subscriber.subscriptionId, token));
        setSubscriberCharges(charges || []);
      } catch (error) {
        console.error('Error fetching subscriber charges:', error);
        notification.error(BackendRpErrorResponse.firstErrorMessage(error));
        setSubscriberCharges([]);
      } finally {
        setLoadingCharges(false);
      }
    }
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedSubscriber(null);
    setSubscriberCharges([]);
  };

  const handleRequestCardUpdate = (subscriber) => {
    setSubscriberForCardUpdate(subscriber);
    setShowRequestCardUpdateModal(true);
  };

  const handleConfirmRequestCardUpdate = async () => {
    if (!subscriberForCardUpdate) return;
    
    try {
      await fetchResourceRP(sendNotification(
        'request_payment_update',
        subscriberForCardUpdate.customerId,
        subscriberForCardUpdate.subscriptionId,
        token
      ));

      gtmEvents.recurringCardUpdateRequestSuccess({
        plan_id: subscriberForCardUpdate.planId,
        merchant_id: merchantId,
      });
      
      setShowRequestCardUpdateModal(false);
      notification.success(__('dashboard.sections.recurringPayments.subscribers.requestCardUpdateModal.successMessage'));
    } catch (error) {
      console.error('Error sending payment update request:', error);
      gtmEvents.recurringCardUpdateRequestError({
        plan_id: subscriberForCardUpdate.planId,
        merchant_id: merchantId,
        error_code: error?.message_code,
      });
      notification.error(__('dashboard.sections.recurringPayments.subscribers.requestCardUpdateModal.errorMessage'));
    }
  };

  const handleCloseRequestCardUpdateModal = () => {
    setShowRequestCardUpdateModal(false);
    setSubscriberForCardUpdate(null);
  };

  const handlePauseSubscription = (subscriber) => {
    setSubscriberToPause(subscriber);
    setShowPauseSubscriptionModal(true);
  };

  const handleConfirmPauseSubscription = async () => {
    if (!subscriberToPause) return;
    
    setIsPausing(true);
    try {
      await fetchResourceRP(pauseSubscription(subscriberToPause.subscriptionId, {}, token));
      
      try {
        await fetchResourceRP(sendNotification(
          'pause_subscription',
          subscriberToPause.customerId,
          subscriberToPause.subscriptionId,
          token
        ));
      } catch (emailError) {
        console.warn('Email notification failed, but subscription was paused successfully', emailError);
        notification.warn(BackendRpErrorResponse.firstErrorMessage(emailError));
      }
      
      gtmEvents.recurringSubscriptionActionSuccess('pause', {
        plan_id: subscriberToPause.planId,
        merchant_id: merchantId,
      });
      setShowPauseSubscriptionModal(false);
      notification.success(__('dashboard.sections.recurringPayments.subscribers.pauseSubscriptionModal.successMessage'));
      
      reload();
      
      if (showDetails && selectedSubscriber?.subscriptionId === subscriberToPause.subscriptionId) {
        handleCloseDetails();
      }
    } catch (error) {
      console.error('Error pausing subscription:', error);
      gtmEvents.recurringSubscriptionActionError('pause', {
        plan_id: subscriberToPause.planId,
        merchant_id: merchantId,
        error_code: error?.message_code,
      });
      notification.error(__('dashboard.sections.recurringPayments.subscribers.pauseSubscriptionModal.errorMessage'));
    } finally {
      setIsPausing(false);
    }
  };

  const handleClosePauseSubscriptionModal = () => {
    setShowPauseSubscriptionModal(false);
    setSubscriberToPause(null);
  };

  const handleCancelSubscription = (subscriber) => {
    setSubscriberToCancel(subscriber);
    setShowCancelSubscriptionModal(true);
  };

  const handleConfirmCancelSubscription = async () => {
    if (!subscriberToCancel) return;
    
    setIsCanceling(true);
    try {
      await fetchResourceRP(cancelSubscription(
        subscriberToCancel.subscriptionId, 
        { cancellation_reason: 'Por el comercio' }, 
        token
      ));
      
      try {
        await fetchResourceRP(sendNotification(
          'cancel_subscription',
          subscriberToCancel.customerId,
          subscriberToCancel.subscriptionId,
          token
        ));
      } catch (emailError) {
        console.warn('Email notification failed, but subscription was cancelled successfully', emailError);
        notification.warn(BackendRpErrorResponse.firstErrorMessage(emailError));
      }
      
      gtmEvents.recurringSubscriptionActionSuccess('cancel', {
        plan_id: subscriberToCancel.planId,
        merchant_id: merchantId,
      });
      setShowCancelSubscriptionModal(false);
      notification.success(__('dashboard.sections.recurringPayments.subscribers.cancelSubscriptionModal.successMessage'));
      
      reload();
      
      if (showDetails && selectedSubscriber?.subscriptionId === subscriberToCancel.subscriptionId) {
        handleCloseDetails();
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      gtmEvents.recurringSubscriptionActionError('cancel', {
        plan_id: subscriberToCancel.planId,
        merchant_id: merchantId,
        error_code: error?.message_code,
      });
      notification.error(__('dashboard.sections.recurringPayments.subscribers.cancelSubscriptionModal.errorMessage'));
    } finally {
      setIsCanceling(false);
    }
  };

  const handleCloseCancelSubscriptionModal = () => {
    setShowCancelSubscriptionModal(false);
    setSubscriberToCancel(null);
  };

  const handleReactivateSubscription = (subscriber) => {
    setSubscriberToReactivate(subscriber);
    setShowReactivateSubscriptionModal(true);
  };

  const handleConfirmReactivateSubscription = async () => {
    if (!subscriberToReactivate) return;
    
    setIsReactivating(true);
    try {
      await fetchResourceRP(reactivateSubscription(subscriberToReactivate.subscriptionId, token));
      
      try {
        await fetchResourceRP(sendNotification(
          'reactivate_subscription',
          subscriberToReactivate.customerId,
          subscriberToReactivate.subscriptionId,
          token
        ));
      } catch (emailError) {
        console.warn('Email notification failed, but subscription was reactivated successfully', emailError);
        notification.warn(BackendRpErrorResponse.firstErrorMessage(emailError));
      }
      
      gtmEvents.recurringSubscriptionActionSuccess('reactivate', {
        plan_id: subscriberToReactivate.planId,
        merchant_id: merchantId,
      });
      setShowReactivateSubscriptionModal(false);
      notification.success(__('dashboard.sections.recurringPayments.subscribers.reactivateSubscriptionModal.successMessage'));
      
      // Recargar la lista de suscriptores
      reload();
      
      // Si está viendo el detalle, cerrarlo
      if (showDetails && selectedSubscriber?.subscriptionId === subscriberToReactivate.subscriptionId) {
        handleCloseDetails();
      }
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      gtmEvents.recurringSubscriptionActionError('reactivate', {
        plan_id: subscriberToReactivate.planId,
        merchant_id: merchantId,
        error_code: error?.message_code,
      });
      notification.error(__('dashboard.sections.recurringPayments.subscribers.reactivateSubscriptionModal.errorMessage'));
    } finally {
      setIsReactivating(false);
    }
  };

  const handleCloseReactivateSubscriptionModal = () => {
    setShowReactivateSubscriptionModal(false);
    setSubscriberToReactivate(null);
  };

  const handleToggleSelected = (subscriptionId) => {
    const subscriber = (items || []).find((item) => item.subscriptionId === subscriptionId);
    setSelectedIds((prev) => {
      if (prev.includes(subscriptionId)) {
        return prev.filter((id) => id !== subscriptionId);
      }
      if (subscriber) rememberSubscriberEmail(subscriber);
      return [...prev, subscriptionId];
    });
  };

  const handleToggleSelectAllPage = () => {
    const pageIds = items.map((item) => item.subscriptionId);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds([]);
      selectedEmailsRef.current = {};
      return;
    }
    (items || []).forEach(rememberSubscriberEmail);
    setSelectedIds(pageIds);
  };

  const handleOpenBulkAction = (action) => {
    // Always default to selected checkboxes. Never auto-switch to "all in filter"
    // when nothing is selected (that caused Pausar(N) with Seleccionados(0)).
    setBulkScope('page');
    setBulkAction(action);
  };

  const handleCloseBulkAction = () => {
    if (isBulkProcessing) return;
    setBulkAction(null);
  };

  const handleConfirmBulkAction = async () => {
    if (!bulkAction || !isPlanFilterInUi) return;
    if (bulkScope === 'page' && selectedIds.length === 0) return;

    setIsBulkProcessing(true);
    try {
      const payload = {
        action: bulkAction,
        plan_id: planFilter,
        scope: bulkScope,
        status: queryParams.status || undefined,
        search: queryParams.search || undefined,
        created_after: queryParams.date_from || undefined,
        created_before: queryParams.date_to || undefined,
      };

      if (bulkScope === 'page') {
        payload.subscription_ids = selectedIds;
      }

      // Snapshot emails before reload; include lowercase keys for GUID casing mismatches.
      const emailBySubscriptionId = {
        ...selectedEmailsRef.current,
        ...(items || []).reduce((acc, subscriber) => {
          const id = subscriber?.subscriptionId;
          const email = subscriber?.customer?.email || subscriber?.email;
          if (id && email) {
            acc[id] = email;
            acc[String(id).toLowerCase()] = email;
          }
          return acc;
        }, {}),
      };

      const result = await fetchResourceRP(bulkSubscriptionAction(payload, token));
      const enrichedResults = (result?.results || []).map((item) => {
        const id = item.subscriptionId;
        const email = item.email
          || emailBySubscriptionId[id]
          || emailBySubscriptionId[String(id)]
          || emailBySubscriptionId[String(id || '').toLowerCase()]
          || null;
        return { ...item, email };
      });

      setBulkAction(null);
      setBulkResultEmails(emailBySubscriptionId);
      setBulkResult({ ...result, results: enrichedResults });
      reload();
    } catch (error) {
      console.error('[SubscribersTab] Error running bulk action:', error);
      notification.error(BackendRpErrorResponse.firstErrorMessage(error));
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleCloseBulkResults = () => {
    setBulkResult(null);
    setBulkResultEmails({});
  };

  return (
    <div className="dashboard-card">
      <BottomAlert {...alertProps} />

      <RequestCardUpdateModal
        showModal={showRequestCardUpdateModal}
        hideModal={handleCloseRequestCardUpdateModal}
        subscriber={subscriberForCardUpdate}
        submitAction={handleConfirmRequestCardUpdate}
      />

      <PauseSubscriptionModal
        showModal={showPauseSubscriptionModal}
        hideModal={handleClosePauseSubscriptionModal}
        subscriber={subscriberToPause}
        submitAction={handleConfirmPauseSubscription}
        submitDisabled={isPausing}
      />

      <ReactivateSubscriptionModal
        showModal={showReactivateSubscriptionModal}
        hideModal={handleCloseReactivateSubscriptionModal}
        subscriber={subscriberToReactivate}
        submitAction={handleConfirmReactivateSubscription}
        submitDisabled={isReactivating}
      />

      <CancelSubscriptionModal
        showModal={showCancelSubscriptionModal}
        hideModal={handleCloseCancelSubscriptionModal}
        subscriber={subscriberToCancel}
        submitAction={handleConfirmCancelSubscription}
        submitDisabled={isCanceling}
      />

      <BulkActionConfirmModal
        showModal={!!bulkAction}
        hideModal={handleCloseBulkAction}
        action={bulkAction}
        scope={bulkScope}
        onScopeChange={setBulkScope}
        pageCount={selectedIds.length}
        filteredCount={data?.pagination?.total_count || 0}
        submitAction={handleConfirmBulkAction}
        submitDisabled={isBulkProcessing}
      />

      <BulkActionResultsModal
        showModal={!!bulkResult}
        hideModal={handleCloseBulkResults}
        result={bulkResult}
        emailBySubscriptionId={bulkResultEmails}
      />

      <DashboardPanel
        panelHeading={__('dashboard.sections.recurringPayments.subscribers.detail.title')}
        expanded={showDetails}
        hidePanel={handleCloseDetails}
      >
        {showDetails && selectedSubscriber && (
          <SubscriberDetailPanel 
            subscriber={selectedSubscriber}
            charges={subscriberCharges}
            loadingCharges={loadingCharges}
            onRequestCardUpdate={handleRequestCardUpdate}
            onPauseSubscription={handlePauseSubscription}
            onCancelSubscription={handleCancelSubscription}
            onReactivateSubscription={handleReactivateSubscription}
            onSubscriberUpdated={handleSubscriberUpdated}
          />
        )}
      </DashboardPanel>

      <div className="dashboard-card-header recurring-payments-card-header">
        {!isLoading && (
          <div className="d-flex align-items-top" style={{ gap: '10px' }}>
            <ServerSideSearch
              value={searchText}
              onChange={setSearchText}
              minLength={3}
              maxLength={100}
            />
            
            <div style={{ width: '180px' }}>
              <SelectInput
                options={[
                  { value: '', label: __('dashboard.sections.recurringPayments.subscribers.filters.period.label') },
                  { value: 'lastDay', label: __('dashboard.sections.recurringPayments.subscribers.filters.period.lastDay') },
                  { value: 'last7Days', label: __('dashboard.sections.recurringPayments.subscribers.filters.period.last7Days') },
                  { value: 'last30Days', label: __('dashboard.sections.recurringPayments.subscribers.filters.period.last30Days') }
                ]}
                value={periodFilter}
                onChange={(value) => setPeriodFilter(value || '')}
                placeholder={__('dashboard.sections.recurringPayments.subscribers.filters.period.label')}
                isSearchable={false}
              />
            </div>
            
            <div style={{ width: '200px' }}>
              <SelectInput
                options={[
                  { value: '', label: __('dashboard.sections.recurringPayments.subscribers.filters.plan.label') },
                  ...uniquePlans.map(plan => ({ value: plan.id, label: plan.name }))
                ]}
                value={planFilter}
                onChange={(value) => {
                  setPlanFilter(value || '');
                  if (!value) {
                    setNavigatedPlan(null);
                  }
                }}
                placeholder={__('dashboard.sections.recurringPayments.subscribers.filters.plan.label')}
                isSearchable={false}
              />
            </div>
            
            <div style={{ width: '180px' }}>
              <SelectInput
                options={[
                  { value: '', label: __('dashboard.sections.recurringPayments.subscribers.filters.status.label') },
                  { value: 'Active', label: __('dashboard.sections.recurringPayments.subscribers.table.statuses.active') },
                  { value: 'Cancelled', label: __('dashboard.sections.recurringPayments.subscribers.table.statuses.cancelled') },
                  { value: 'Paused', label: __('dashboard.sections.recurringPayments.subscribers.table.statuses.paused') },
                  { value: 'Expired', label: __('dashboard.sections.recurringPayments.subscribers.table.statuses.expired') },
                  { value: 'PastDue', label: __('dashboard.sections.recurringPayments.subscribers.table.statuses.pastDue') }
                ]}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value || '')}
                placeholder={__('dashboard.sections.recurringPayments.subscribers.filters.status.label')}
                isSearchable={false}
              />
            </div>
            
            {hasActiveFilters && (
              <Button
                color="link"
                onClick={clearFilters}
                style={{ whiteSpace: 'nowrap' }}
              >
                {__('dashboard.sections.recurringPayments.subscribers.filters.clearFilters')}
              </Button>
            )}
          </div>
        )}
        <div className="ml-auto d-flex align-items-top">
          {isPlanFilterInUi && selectedIds.length > 0 && (
            <div className="mr-2">
              <BulkActionsWidget
                disabled={isBulkProcessing}
                onAction={handleOpenBulkAction}
              />
            </div>
          )}
          {data?.data && data.data.length > 0 && (
            <div className="mr-2">
              <SubscribersReportWidget currentFilters={queryParams} />
            </div>
          )}
        </div>
      </div>
      
      <div className="dashboard-card-body">
        {(isLoading || showLoader) && <Loader />}
        {(!items.length && !isLoading && !showLoader) && (
          <div className="dashboard-card-message">
            <div className="d-flex justify-content-center mb-3">
              <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '120px', height: '120px' }}>
                <i className="mdi mdi-account-multiple" style={{ fontSize: '60px', color: '#999' }} />
              </div>
            </div>
            <h3>{__('dashboard.sections.recurringPayments.subscribers.noSubscribers')}</h3>
            <p>{__('dashboard.sections.recurringPayments.subscribers.noSubscribersDescription')}</p>
          </div>
        )}
        {(items.length > 0 && !isLoading && !showLoader) && (
          <>
            <SubscribersTable
              subscribers={items}
              onRowClick={handleRowClick}
              onRequestCardUpdate={handleRequestCardUpdate}
              onPauseSubscription={handlePauseSubscription}
              onCancelSubscription={handleCancelSubscription}
              onReactivateSubscription={handleReactivateSubscription}
              selectedIds={selectedIds}
              onToggleSelected={handleToggleSelected}
              onToggleSelectAll={handleToggleSelectAllPage}
            />
            
            {/* Controles de paginación */}
            {data?.pagination && data.pagination.total_count > pageSize && (
              <div className="d-flex justify-content-between align-items-center mt-3 px-3 pb-3">
                <div className="text-muted">
                  {__('dashboard.sections.recurringPayments.subscribers.showing')} {' '}
                  {Math.min((currentPage - 1) * pageSize + 1, data.pagination.total_count)} - {' '}
                  {Math.min(currentPage * pageSize, data.pagination.total_count)} {' '}
                  {__('dashboard.sections.recurringPayments.subscribers.of')} {data.pagination.total_count}
                </div>
                <div className="d-flex align-items-center">
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => {
                      setShowLoader(true);
                      setCurrentPage(p => Math.max(1, p - 1));
                    }}
                    className="mr-2 d-flex align-items-center justify-content-center"
                    style={{ minWidth: '32px', minHeight: '32px' }}
                  >
                    <i className="mdi mdi-chevron-left" style={{ lineHeight: 1 }} />
                  </Button>
                  <span className="mx-2">
                    {__('dashboard.sections.recurringPayments.subscribers.page')} {currentPage} {' '}
                    {__('dashboard.sections.recurringPayments.subscribers.of')} {' '}
                    {Math.ceil(data.pagination.total_count / pageSize)}
                  </span>
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    disabled={currentPage >= Math.ceil(data.pagination.total_count / pageSize)}
                    onClick={() => {
                      setShowLoader(true);
                      setCurrentPage(p => p + 1);
                    }}
                    className="ml-2 d-flex align-items-center justify-content-center"
                    style={{ minWidth: '32px', minHeight: '32px' }}
                  >
                    <i className="mdi mdi-chevron-right" style={{ lineHeight: 1 }} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
